import { MapPin, X } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactElement, type Ref } from 'react';
import type { CardDetail } from '@molt/shared';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Popover } from '@/components/Popover';
import { TextField } from '@/components/TextField';
import {
  normaliseMapCoordinateInput,
  useRemoveCardLocation,
  useSetCardLocation,
} from '@/lib/board-map';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { announce } from '@/lib/announce';

export interface CardLocationSectionProps {
  readonly card: CardDetail;
  readonly boardId: string;
  readonly canEdit: boolean;
  /**
   * Render ONLY the trigger control, for the card back's "Add to card" row
   * (`docs/design/stage2/card-back.md` D1): no heading, no empty-state prose.
   * The trigger keeps its accessible name and its popover is unchanged. A
   * viewer who cannot edit gets nothing, because an empty section with no
   * control in it says nothing the card back needs to say.
   */
  readonly compact?: boolean | undefined;
  /**
   * The trigger, so the card back can focus it when this section first moves
   * into the body because the member just filled it from its compact trigger
   * in the "Add to card" row, which unmounted with focus inside it.
   */
  readonly triggerRef?: Ref<HTMLButtonElement> | undefined;
}

export function CardLocationSection({
  card,
  boardId,
  canEdit,
  compact = false,
  triggerRef = null,
}: CardLocationSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const setLocation = useSetCardLocation();
  const removeLocation = useRemoveCardLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(card.location?.text ?? '');
  const [lat, setLat] = useState(card.location === null ? '' : String(card.location.lat));
  const [lng, setLng] = useState(card.location === null ? '' : String(card.location.lng));
  const [error, setError] = useState<string | null>(null);
  const label =
    card.location === null
      ? t('cardBack.location.noLocation')
      : (card.location.text ??
        t('cardBack.location.coordinates', {
          lat: card.location.lat,
          lng: card.location.lng,
        }));
  const title = card.title.trim() || t('cardBack.untitledCard');

  useEffect(() => {
    if (!open) return;
    setText(card.location?.text ?? '');
    setLat(card.location === null ? '' : String(card.location.lat));
    setLng(card.location === null ? '' : String(card.location.lng));
    setError(null);
  }, [card.location, open]);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const parsedLat = normaliseMapCoordinateInput(lat, 'lat');
    const parsedLng = normaliseMapCoordinateInput(lng, 'lng');
    if (parsedLat === null || parsedLng === null) {
      setError(t('cardBack.location.invalidCoordinates'));
      return;
    }
    setLocation.mutate(
      {
        cardId: card.id,
        boardId,
        location: {
          text: text.trim().length === 0 ? null : text,
          lat: parsedLat,
          lng: parsedLng,
        },
      },
      {
        onSuccess: () => {
          announce(t('cardBack.location.announce.saved'));
          setOpen(false);
        },
        onError: (cause) => {
          setError(
            cause instanceof Error ? cause.message : t('cardBack.location.saveFailed'),
          );
        },
      },
    );
  }

  function clearLocation(): void {
    removeLocation.mutate(
      { cardId: card.id, boardId },
      {
        onSuccess: () => {
          announce(t('cardBack.location.announce.removed'));
          setOpen(false);
        },
        onError: (cause) => {
          setError(
            cause instanceof Error ? cause.message : t('cardBack.location.removeFailed'),
          );
        },
      },
    );
  }

  const editor = canEdit ? (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title={t('cardBack.location.popoverTitle')}
      width={360}
      trigger={
        <Button
          ref={triggerRef}
          type="button"
          size="sm"
          variant="secondary"
          aria-label={t('cardBack.location.trigger', { title })}
          iconStart={<MapPin aria-hidden="true" className="size-4" />}
        >
          {t('cardBack.location.heading')}
        </Button>
      }
      closeControl={
        <IconButton label={t('cardBack.location.closePopover')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
    >
      <form
        aria-label={t('cardBack.location.popoverTitle')}
        className="flex flex-col gap-3"
        onSubmit={submit}
      >
        <TextField
          label={t('cardBack.location.addressField')}
          value={text}
          maxLength={512}
          hint={t('cardBack.location.addressHint')}
          onChange={(event) => setText(event.currentTarget.value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <TextField
            label={t('cardBack.location.latitudeField')}
            type="number"
            step="0.0000001"
            min={-90}
            max={90}
            value={lat}
            required
            onChange={(event) => setLat(event.currentTarget.value)}
          />
          <TextField
            label={t('cardBack.location.longitudeField')}
            type="number"
            step="0.0000001"
            min={-180}
            max={180}
            value={lng}
            required
            onChange={(event) => setLng(event.currentTarget.value)}
          />
        </div>
        {error === null ? null : <p className="text-card text-bad">{error}</p>}
        <div className="flex flex-wrap justify-between gap-2">
          <Button
            type="button"
            size="sm"
            variant="quiet"
            disabled={card.location === null || removeLocation.isPending}
            busy={removeLocation.isPending}
            onClick={clearLocation}
          >
            {t('cardBack.location.remove')}
          </Button>
          <Button type="submit" size="sm" variant="primary" busy={setLocation.isPending}>
            {t('cardBack.location.save')}
          </Button>
        </div>
      </form>
    </Popover>
  ) : null;

  if (compact) return editor;

  return (
    <section aria-labelledby="card-location-heading" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="card-location-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.location.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {editor}
      </div>
      {card.location === null ? (
        <p className="text-card text-ink-muted">{label}</p>
      ) : (
        <div className="rounded-card border border-border bg-surface-raised px-3 py-2 text-card">
          <p className="font-medium text-ink">{label}</p>
          <p className="text-ink-muted">
            {t('cardBack.location.coordinates', {
              lat: card.location.lat,
              lng: card.location.lng,
            })}
          </p>
        </div>
      )}
    </section>
  );
}
